<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <v-row>
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-4">Scraper Schedule Management</h1>
          </v-col>
          <v-col cols="12" md="4" class="text-right">
            <v-btn color="green" dark @click="openAddDialog">
              <v-icon left class="material-icons-round">add</v-icon>
              Add Schedule
            </v-btn>
          </v-col>
        </v-row>

        <!-- Schedules Table -->
        <v-row>
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl mb-4">
              <v-card-title class="d-flex justify-space-between">
                <span>Active Schedules</span>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Search schedules..."
                  single-line
                  hide-details
                  dense
                  outlined
                  style="max-width: 300px;"
                ></v-text-field>
              </v-card-title>

              <v-card-text>
                <v-data-table
                  :headers="scheduleHeaders"
                  :items="schedules"
                  :search="search"
                  :loading="loading"
                  class="elevation-1"
                  :items-per-page="10"
                >
                  <template v-slot:item.is_active="{ item }">
                    <v-chip :color="item.is_active ? 'success' : 'error'" small>
                      {{ item.is_active ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>

                  <template v-slot:item.frequency="{ item }">
                    <v-chip color="primary" small>
                      {{ item.frequency.charAt(0).toUpperCase() + item.frequency.slice(1) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.last_run_at="{ item }">
                    <span v-if="item.last_run_at">
                      {{ formatDate(item.last_run_at) }}
                    </span>
                    <span v-else class="text--secondary">Never</span>
                  </template>

                  <template v-slot:item.next_run_at="{ item }">
                    <span v-if="item.next_run_at && item.is_active">
                      {{ formatDate(item.next_run_at) }}
                    </span>
                    <span v-else class="text--secondary">-</span>
                  </template>

                  <template v-slot:item.latest_run="{ item }">
                    <v-chip 
                      v-if="item.latest_run"
                      :color="getRunStatusColor(item.latest_run.status)"
                      small
                    >
                      {{ item.latest_run.status.charAt(0).toUpperCase() + item.latest_run.status.slice(1) }}
                    </v-chip>
                    <span v-else class="text--secondary">No runs</span>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="runNow(item)" v-bind="attrs" v-on="on" :loading="item.running">
                          <v-icon small class="material-icons-round">play_arrow</v-icon>
                        </v-btn>
                      </template>
                      <span>Run Now</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="toggleStatus(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">
                            {{ item.is_active ? 'pause' : 'play_arrow' }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ item.is_active ? 'Pause' : 'Activate' }}</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="viewRunHistory(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">history</v-icon>
                        </v-btn>
                      </template>
                      <span>View History</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="editSchedule(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">edit</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit</span>
                    </v-tooltip>

                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small @click="deleteSchedule(item)" v-bind="attrs" v-on="on">
                          <v-icon small class="material-icons-round">delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <!-- Add/Edit Schedule Dialog -->
      <v-dialog v-model="scheduleDialog" max-width="600px" persistent>
        <v-card>
          <v-card-title>
            {{ editIndex === -1 ? 'Add New Schedule' : 'Edit Schedule' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="scheduleForm" v-model="formValid">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedSchedule.name"
                    label="Schedule Name"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Name is required']"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="editedSchedule.dealer"
                    :items="dealerOptions"
                    label="Dealer"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Dealer is required']"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedSchedule.url"
                    label="Scraper URL"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'URL is required', v => isValidUrl(v) || 'Invalid URL format']"
                    placeholder="https://www.autodealzaz.com/cars-for-sale"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="editedSchedule.frequency"
                    :items="frequencyOptions"
                    label="Frequency"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Frequency is required']"
                    @change="onFrequencyChange"
                  ></v-select>
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="editedSchedule.scheduled_time"
                    label="Scheduled Time"
                    type="time"
                    outlined
                    dense
                    required
                    :rules="[v => !!v || 'Time is required']"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" md="6" v-if="!['6hours', '12hours', 'daily'].includes(editedSchedule.frequency)">
                  <v-text-field
                    v-model.number="editedSchedule.scheduled_day"
                    :label="editedSchedule.frequency === 'weekly' ? 'Day of Week (1-7, Monday=1)' : 'Day of Month (1-31)'"
                    type="number"
                    :min="1"
                    :max="editedSchedule.frequency === 'weekly' ? 7 : 31"
                    outlined
                    dense
                    :required="!['6hours', '12hours', 'daily'].includes(editedSchedule.frequency)"
                    :rules="dayRules"
                    :hint="editedSchedule.frequency === 'weekly' ? 'Monday=1, Tuesday=2, ..., Sunday=7' : 'Day of the month (1-31)'"
                    persistent-hint
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="closeScheduleDialog">Cancel</v-btn>
            <v-btn color="green" dark @click="saveSchedule" :disabled="!formValid" :loading="saving">
              {{ editIndex === -1 ? 'Create' : 'Update' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-main>
    <vue-footer-admin></vue-footer-admin>
  </v-app>
</template>

<script>
import moment from 'moment'
import Vue from 'vue'

export default {
  name: 'ScheduleAdmin',
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      schedules: [],
      scheduleDialog: false,
      editIndex: -1,
      formValid: false,
      
      editedSchedule: {
        name: '',
        dealer: 'AutoDealzAZ',
        url: 'https://www.autodealzaz.com/cars-for-sale',
        frequency: 'daily',
        scheduled_time: '09:00',
        scheduled_day: null
      },

      defaultSchedule: {
        name: '',
        dealer: 'AutoDealzAZ', 
        url: 'https://www.autodealzaz.com/cars-for-sale',
        frequency: 'daily',
        scheduled_time: '09:00',
        scheduled_day: null
      },

      scheduleHeaders: [
        { text: 'Name', value: 'name' },
        { text: 'Dealer', value: 'dealer' },
        { text: 'Frequency', value: 'frequency' },
        { text: 'Scheduled Time', value: 'scheduled_time' },
        { text: 'Status', value: 'is_active' },
        { text: 'Last Run', value: 'last_run_at' },
        { text: 'Next Run', value: 'next_run_at' },
        { text: 'Latest Status', value: 'latest_run' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],

      dealerOptions: [
        { text: 'AutoDealzAZ', value: 'AutoDealzAZ' }
      ],

      frequencyOptions: [
        { text: 'Every 6 Hours', value: '6hours' },
        { text: 'Every 12 Hours', value: '12hours' },
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' }
      ]
    }
  },

  computed: {
    dayRules() {
      if (['6hours', '12hours', 'daily'].includes(this.editedSchedule.frequency)) {
        return [] // No day validation needed for hourly/daily schedules
      } else if (this.editedSchedule.frequency === 'weekly') {
        return [
          v => !!v || 'Day is required',
          v => (v >= 1 && v <= 7) || 'Day must be between 1-7 (1=Monday, 7=Sunday)'
        ]
      } else if (this.editedSchedule.frequency === 'monthly') {
        return [
          v => !!v || 'Day is required',
          v => (v >= 1 && v <= 31) || 'Day must be between 1-31'
        ]
      }
      return []
    }
  },

  mounted() {
    this.fetchSchedules()
  },

  methods: {
    async fetchSchedules() {
      this.loading = true
      try {
        const response = await axios.get('/api/schedules', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })

        if (response.data.success) {
          this.schedules = response.data.schedules
        }
      } catch (error) {
        console.error('Error fetching schedules:', error)
        Vue.$toast.error('Failed to load schedules', {
          position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },

    openAddDialog() {
      this.editIndex = -1
      this.editedSchedule = Object.assign({}, this.defaultSchedule)
      this.scheduleDialog = true
    },

    onFrequencyChange() {
      // Reset scheduled_day when frequency changes
      if (['6hours', '12hours', 'daily'].includes(this.editedSchedule.frequency)) {
        this.editedSchedule.scheduled_day = null
      } else if (this.editedSchedule.frequency === 'weekly') {
        this.editedSchedule.scheduled_day = 1 // Default to Monday
      } else if (this.editedSchedule.frequency === 'monthly') {
        this.editedSchedule.scheduled_day = 1 // Default to 1st of month
      }
    },

    editSchedule(item) {
      this.editIndex = this.schedules.indexOf(item)
      this.editedSchedule = Object.assign({}, item)
      this.scheduleDialog = true
    },

    closeScheduleDialog() {
      this.scheduleDialog = false
      this.$refs.scheduleForm.resetValidation()
    },

    async saveSchedule() {
      if (!this.$refs.scheduleForm.validate()) return

      this.saving = true
      try {
        // Prepare the data to send
        const scheduleData = {
          name: this.editedSchedule.name,
          dealer: this.editedSchedule.dealer,
          url: this.editedSchedule.url,
          frequency: this.editedSchedule.frequency,
          scheduled_time: this.editedSchedule.scheduled_time,
          scheduled_day: ['6hours', '12hours', 'daily'].includes(this.editedSchedule.frequency) ? null : this.editedSchedule.scheduled_day
        }

        console.log('Sending schedule data:', scheduleData)
        console.log('Request URL:', this.editIndex === -1 ? '/api/schedules' : `/api/schedules/${this.editedSchedule.id}`)

        let response
        if (this.editIndex === -1) {
          // Create new schedule
          response = await axios.post('/api/schedules', scheduleData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        } else {
          // Update existing schedule
          response = await axios.put(`/api/schedules/${this.editedSchedule.id}`, scheduleData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          })
        }

        if (response.data.success) {
          Vue.$toast.success(response.data.message, {
            position: 'top-right'
          })
          this.closeScheduleDialog()
          this.fetchSchedules()
        } else {
          Vue.$toast.error(response.data.message || 'Failed to save schedule', {
            position: 'top-right'
          })
        }
      } catch (error) {
        console.error('Error saving schedule:', error)
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = error.response.data.errors
          const errorMessages = Object.values(errors).flat().join(', ')
          Vue.$toast.error(`Validation errors: ${errorMessages}`, {
            position: 'top-right'
          })
        } else if (error.response && error.response.data && error.response.data.message) {
          Vue.$toast.error(error.response.data.message, {
            position: 'top-right'
          })
        } else {
          Vue.$toast.error('Failed to save schedule', {
            position: 'top-right'
          })
        }
      } finally {
        this.saving = false
      }
    },

    async deleteSchedule(item) {
      if (confirm('Are you sure you want to delete this schedule?')) {
        try {
          const response = await axios.delete(`/api/schedules/${item.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          })

          if (response.data.success) {
            Vue.$toast.success(response.data.message, {
              position: 'top-right'
            })
            this.fetchSchedules()
          }
        } catch (error) {
          console.error('Error deleting schedule:', error)
          Vue.$toast.error('Failed to delete schedule', {
            position: 'top-right'
          })
        }
      }
    },

    async toggleStatus(item) {
      try {
        const response = await axios.post(`/api/schedules/${item.id}/toggle`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })

        if (response.data.success) {
          Vue.$toast.success(response.data.message, {
            position: 'top-right'
          })
          this.fetchSchedules()
        }
      } catch (error) {
        console.error('Error toggling schedule status:', error)
        Vue.$toast.error('Failed to update schedule status', {
          position: 'top-right'
        })
      }
    },

    async runNow(item) {
      this.$set(item, 'running', true)
      try {
        const response = await axios.post(`/api/schedules/${item.id}/run`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })

        if (response.data.success) {
          Vue.$toast.success(response.data.message, {
            position: 'top-right'
          })
          // Refresh schedules after a short delay to show updated status
          setTimeout(() => {
            this.fetchSchedules()
          }, 2000)
        }
      } catch (error) {
        console.error('Error running schedule:', error)
        Vue.$toast.error('Failed to run schedule', {
          position: 'top-right'
        })
      } finally {
        this.$set(item, 'running', false)
      }
    },

    viewRunHistory(item) {
      this.$router.push(`/admin/schedule-history/${item.id}`)
    },

    formatDate(dateString) {
      return moment(dateString).format('MMM DD, YYYY HH:mm')
    },

    getRunStatusColor(status) {
      switch (status) {
        case 'completed': return 'success'
        case 'failed': return 'error'
        case 'started': return 'warning'
        default: return 'grey'
      }
    },

    isValidUrl(url) {
      try {
        new URL(url)
        return true
      } catch {
        return false
      }
    }
  }
}
</script>

<style scoped>
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
  border: 1px solid #ccc;
}
</style>
